import {ButtonWithIcon} from '@/components/atoms'
import {AccordionItemCustom as AccordionItem} from '@/components/molecules/AccordionItemCustom'
import {PromotionStep} from '@/types/promotion'
import {COLOR} from '@/utils/constants/colors'
import {SIZE} from '@/utils/constants/globalStyle'
import {LabelWithVal} from './LabelWithVal'

/**
 * @typedef {import('@/types/promotionTypes').CreatePromotionRequest} CreatePromotionRequest
 */

/**
 * Review component for displaying promotion setup information
 * @param {Object} props
 * @param {CreatePromotionRequest} props.promotionReviewData - The promotion data to review
 * @param {boolean} props.isDetailView - Whether this is a detail view (read-only)
 * @param {Function} props.handleEditStep - Callback to edit a specific step
 * @returns {JSX.Element}
 */
const PromotionsReviewSetup = ({promotionReviewData, handleEditStep, showEdit}) => {
  const {segmentConfiguration, allCustomers} = promotionReviewData || {}

  const getExcludedItemsDisplay = () => {
    let excludedItems = promotionReviewData?.excludedItems

    if (!Array.isArray(excludedItems) || excludedItems.length === 0) {
      return '-'
    }

    const excludedItem = excludedItems?.[0] || {items: [], itemType: ''}

    if (!excludedItem?.items || excludedItem.items.length === 0) {
      return '-'
    }

    return excludedItem.items.map(item => item.name || item.id).join(', ')
  }

  const getProductCombination = () => {
    const firstCondition = promotionReviewData?.promotionConditions?.[0]?.conditionGroup?.conditions?.[0]
    return firstCondition?.productCombination ? 'Yes' : 'No'
  }

  const getAllCustomersValue = () => {
    return allCustomers ? 'Yes' : 'No'
  }

  const getExcludedSegmentsDisplay = () => {
    const excludedSegments = promotionReviewData?.segmentConfiguration?.excludedSegment?.segments
    if (excludedSegments?.length > 0) {
      return excludedSegments.map(seg => seg.segmentName || seg.segmentId).join(', ')
    }
    return '-'
  }

  const renderSegmentContent = () => {
    // 1. ALL_CUSTOMERS – display with excluded segments
    if (allCustomers) {
      return (
        <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
          <LabelWithVal keyName="All Customers" val={getAllCustomersValue()} />
          <LabelWithVal keyName="Excluded Segments" val={getExcludedSegmentsDisplay()} />
        </div>
      )
    }

    // 2. All other segment selections - using new segmentConfiguration format
    const includedSegments = segmentConfiguration?.includedSegments || []

    return (
      <>
        {/* First row – All customers */}
        <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
          <LabelWithVal keyName="All Customers" val={getAllCustomersValue()} />
        </div>

        {/* Segment groups grid */}
        {includedSegments.length > 0 && (
          <div className="mt-4 grid w-full grid-cols-1 gap-6 overflow-hidden md:grid-cols-2 lg:grid-cols-3">
            {includedSegments.map((group, idx) => {
              const segmentNames = (group?.segments || []).map(seg => seg.segmentName || seg.segmentId)
              const display = segmentNames.length > 0 ? segmentNames.join(', ') : '-'
              return (
                <div key={idx} className="mb-4">
                  <div
                    className="mb-2 text-xs font-normal"
                    style={{fontFamily: 'var(--body-font)', letterSpacing: 0, verticalAlign: 'middle'}}
                  >
                    Group {idx + 1}
                  </div>
                  <div
                    className="text-sm font-bold"
                    style={{fontFamily: 'var(--body-font)', letterSpacing: 0, verticalAlign: 'middle'}}
                  >
                    {display}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Excluded Segments */}
        <div className="mt-2 w-full overflow-hidden pb-5">
          <LabelWithVal keyName="Excluded Segments" val={getExcludedSegmentsDisplay()} />
        </div>
      </>
    )
  }

  return (
    <AccordionItem
      isDark
      expanded
      size="large"
      className="min-h-12 w-full gap-2 rounded p-4"
      labelClassName="font-body text-base font-medium leading-normal"
      label={
        <div className="flex w-full items-center justify-between">
          <div className="font-medium">Setup</div>
        </div>
      }
      rightAccessoryChild={
        showEdit ? (
          <ButtonWithIcon
            iconLeading="edit"
            handleButton={() => handleEditStep(PromotionStep.SETUP)}
            uiSchema={{
              className: 'flex items-center text-sm text-[#0069D7] border-none ml-4',
              title: 'Edit',
              size: SIZE.SMALL,
              variant: COLOR.TERTIARY,
              isDark: true,
            }}
          />
        ) : null
      }
      presentation="test"
      content={
        <div
          className="promotions-content-wrapper w-full overflow-hidden"
          style={{padding: '12px 16px', gap: '16px', minWidth: '1238px'}}
        >
          {/* Promotion Subsection */}
          <div className="promotion-subsection w-full">
            <div className="sc-border heading pb-5 text-base font-bold">Promotion</div>
            <div className="grid w-full grid-cols-1 gap-6 overflow-hidden pb-5">
              <LabelWithVal keyName="Name" val={promotionReviewData?.name} />
            </div>
            <div className="grid w-full grid-cols-3 gap-6 overflow-hidden pb-5">
              <LabelWithVal keyName="Promotion" val={promotionReviewData?.promotionName || '-'} />
              <LabelWithVal keyName="Promotion Excludes" val={getExcludedItemsDisplay()} />
              <LabelWithVal keyName="Combination of Products" val={getProductCombination()} />
            </div>
          </div>

          {/* Segments Subsection */}
          <div className="segments-subsection mt-6 w-full">
            <div className="sc-border heading pb-5 text-base font-bold">Segments</div>
            {renderSegmentContent()}
          </div>
        </div>
      }
    />
  )
}

export default PromotionsReviewSetup
